import type {} from '@core/veyopRuntime';
import type { DesignItem } from '@core/types';
import { eventBus } from '@core/EventBus';
import { buildPreflightReport, normalizePreflightProfile, setPreflightServerDefaults } from './engine/preflight';
import PreflightStatusPanel from './PreflightStatusPanel.vue';
import PreflightOverlay from './PreflightOverlay.vue';
import { expandPreflight } from './uiState';
import { imageDpiForItem } from './imageDpi';

let registered = false;

function registerOnVeyop(Veyop: NonNullable<typeof window.Veyop>): void {
  if (registered || !Veyop.preflight || !Veyop.designer?.registerStatusPill) return;
  registered = true;

  Veyop.preflight.buildReport = buildPreflightReport as unknown as typeof Veyop.preflight.buildReport;
  Veyop.preflight.normalizeProfile = normalizePreflightProfile as unknown as typeof Veyop.preflight.normalizeProfile;

  Veyop.designer.registerStatusPill({
    id: 'preflight',
    priority: 10,
    render: (ctx) => ({ component: PreflightStatusPanel, props: { mobile: ctx.mobile } }),
  });

  Veyop.designer.registerOverlay({
    id: 'preflight-overlay',
    priority: 40,
    render: (ctx) => ({ component: PreflightOverlay, props: { context: ctx } }),
  });

  eventBus.on('veyop:preflight:expand-request', expandPreflight);

  // Live print resolution for editors that change an image's size or crop (e.g. the cropper),
  // computed exactly like the low-DPI check.
  Veyop.hooks?.addFilter?.('image.print.dpi', 'preflight', (value, item) =>
    imageDpiForItem(item as DesignItem) ?? value);

  const hydrateConfig = (payload: unknown) => {
    const config = (payload as { config?: { items?: { preflightDefaults?: unknown } } })?.config;
    setPreflightServerDefaults(config?.items?.preflightDefaults);
  };
  if (Veyop.hooks?.whenReady) Veyop.hooks.whenReady('designer', hydrateConfig);
  else Veyop.hooks?.on?.('veyop:designer:ready', hydrateConfig);
}

function boot(): void {
  const tryRegister = () => {
    if (window.Veyop) registerOnVeyop(window.Veyop);
  };

  window.Veyop?.hooks?.on?.('veyop:designer:ready', tryRegister);
  tryRegister();

  const start = Date.now();
  const tick = () => {
    tryRegister();
    if (registered || Date.now() - start > 5000) return;
    setTimeout(tick, 50);
  };
  tick();
}

boot();
